import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { RouterService } from '../../../../utilities/router/router.service';
import { AreaService } from '../../../../resources/area/area.service';
import { Area } from '../../../../resources/area/area.interface';
import { Project } from '../../../../resources/project/project.interface';
import { Building } from '../../../../resources/building/building.interface';
import { BasicArea } from '../../../../resources/basic-area/basic-area.interface';
import { BasicAreaService } from '../../../../resources/basic-area/basic-area.service';
import { Cluster } from '../../../../resources/cluster/cluster.interface';
import { ClusterService } from '../../../../resources/cluster/cluster.service';
import { FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ProjectModule } from '../../../../resources/module/project-module.interface';
import { ProjectElement } from '../../../../resources/element/project-element.interface';
import { ScrollToService } from '../../../../utilities/scroll-to/scroll-to.service';
import { Subject } from 'rxjs';
import { ElementService } from '../../../../resources/element/element.service';
import { takeUntil, skip } from 'rxjs/operators';
import { ElementSidebarService } from '../../../../resources/element/element-sidebar/element-sidebar.service';
import { ConstantsRequirementType, ConstantsRequirementStrictness } from '../../../../utilities/constants/constants.interface';
import { ConstantsService } from '../../../../utilities/constants/constants.service';
import { ModuleService } from '../../../../resources/module/module.service';
import { AuthService } from '../../../../auth/auth.service';
import { Resource } from '../../../../resources/resource.interface';

interface GroupedAreasList {
  name: string;
  areas: Area[];
}

@Component({
  selector: 'sg2-area-details-view',
  templateUrl: './area-details-view.component.html',
  styleUrls: ['./area-details-view.component.less']
})
export class AreaDetailsViewComponent implements OnDestroy, OnInit {

  /** Current area. */
  public area: Area | BasicArea;

  /** Selected Area */
  public selectedArea: Area;

  /** Dropdown for filtering current modules in view. */
  public areaFilterForm: FormControl;

  /** Current basic area. */
  public basicArea: BasicArea;

  /** Unsubscribe hook. */
  private destroy$ = new Subject();

  /** Page type/variant. */
  public pagetype: 'area' | 'basicArea';

  /** Route params. */
  private params: {
    areaId: Area['_id'],
    basicAreaId: BasicArea['_id'],
    buildingId: Building['_id'],
    projectId: Project['_id'],
  };

  /** Filtered list of all requirements for the current area. */
  public requirements: ProjectModule[];

  /** Clusters. */
  public clusters: Cluster[];

  /** Requirement types. */
  public requirementTypes: ConstantsRequirementType[];

  /** Requirements table columns/headers. */
  public tableColumns = ['icon', 'code', 'name', 'value', 'originalValue', 'unit', 'edited', 'status', 'assignedTo', 'options'];

  constructor(
    @Inject('$sgLocation') private $sgLocation: any,
    public authService: AuthService,
    private areaService: AreaService,
    private basicAreaService: BasicAreaService,
    private clusterService: ClusterService,
    private constantsService: ConstantsService,
    private elementService: ElementService,
    public elementSidebarService: ElementSidebarService,
    private moduleService: ModuleService,
    private routerService: RouterService,
    private scrollToService: ScrollToService,
  ) { }

  ngOnInit() {
    this._setPageType();
    this.params = this.routerService.getParams();
    this.requirementTypes = this.constantsService.getRequirementTypes();

    this._setData();
    this._initForm();
    this._onAreaFilterChange();
    this._onRequirementChange();
    this._scrollToOnInit();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Add new requirement.
   *
   * The new requirement is created from a basic resource template and a resource
   * template form controls object.
   *
   * @todo Create a basic resource template interface. See TemplateFormControls interface.
   *
   * @param moduleId Module id.
   * @param submoduleId Submodule id.
   */
  public addRequirement(moduleId: ProjectElement['moduleId'], submoduleId: ProjectElement['submoduleId']): void {
    this.elementSidebarService.new({
      constructionId: this.area._id,
      hasMultipleQualityLevels: false,
      inputType: 'String',
      isCustom: true,
      moduleId: moduleId,
      projectLevel: 5,
      requirementType: this._getRequirementType(moduleId),
      submoduleId: submoduleId,
      value: ''
    }, {
      projectLevel: new FormControl({ value: 5, disabled: true }),
      constructionId: new FormControl({ value: this.area._id, disabled: true }),
      knowledgeLevel: new FormControl({ value: '', disabled: true }),
      moduleId: new FormControl({ value: moduleId, disabled: true }),
      submoduleId: new FormControl({ value: submoduleId, disabled: true }),
    });
  }

  /**
   * Go to legacy basic area overview.
   *
   * @todo Modify once the target page is refactored.
   */
  public goToLegacyBasicAreas(): void {
    this.$sgLocation.path(`/project/${ this.params.projectId }/basic-areas`);
  }

  /**
   * Go to legacy building.
   *
   * @todo Modify once the target page is refactored.
   * @todo Add cluster id as anchor to building page.
   */
  public goToLegacyBuilding(): void {
    document.location.href = `/v2/project/${ this.params.projectId }/areas/area-scheme`;
  }

  /**
   * Go to the area detail page
   */
  public goToSelectedArea(): void {
    this.$sgLocation.path(`/project/${ this.params.projectId }/building/${ this.params.buildingId }/areas/${ this.selectedArea._id }`);
  }

  /**
   * Check if the module contains requirements.
   *
   * @note The check is only performed for area templates.
   *
   * @param module The module with the requirements.
   * @returns Whether the area template module contains requirements.
   */
  public moduleContainsRequirements(module: ProjectModule): boolean {
    if (this.pagetype === 'area') {
      return true;
    }
    for (const submodule of module.submodules) {
      if (submodule.requirements.length > 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * Set area (template) name.
   *
   * @param $event The event that fires on submit.
   * @param type The type of entity to update.
   * @param entity Entity object reference for easy access.
   */
  public submitTitle($event: KeyboardEvent, type: Resource['entity'], entity: Area | BasicArea): void {
    $event.preventDefault();
    let title = ($event.target as HTMLSpanElement).innerText.trim();

    if (entity.name === title) {
      return;
    }
    if (title.length === 0) {
      title = entity.name;
      ($event.target as HTMLSpanElement).innerText = entity.name;
    }
    (type === 'area')
      ? this.areaService.setName(entity as Area, title)
      : this.basicAreaService.setName(entity as BasicArea, title);
  }

  /**
   * Cancel and revert name change.
   *
   * @param $event The event that fires on submit.
   * @param entity Entity object reference for easy access.
   */
  public submitTitleCancel($event: KeyboardEvent, entity: Area | BasicArea): void {
    ($event.target as HTMLSpanElement).innerText = entity.name;
  }

  /**
   * Get status type id for a given `viewtab` property.
   *
   * @note This would be better to handle in the module service, where the viewtab
   *  property is either replaced by the requirement type id (or added as a new seperate
   *  property). This should not be handles in the controller.
   */
  private _getRequirementType(moduleId: ProjectModule['_id']): ConstantsRequirementType['_id'] {
    const module = this.moduleService.getModule(moduleId);
    return this.constantsService.getRequirementTypes().find(rt => rt.value === module.viewtab)._id;
  }

  /**
   * Initialize forms.
   *
   * - Area filter form.
   * - Area properties form.
   */
  private _initForm(): void {
    this.areaFilterForm = new FormControl('all');
  }

  /**
   * Track changes on area filter dropdown.
   */
  private _onAreaFilterChange(): void {
    this.areaFilterForm.valueChanges.subscribe(value => {
      this.scrollToService.find('anchor');
    });
  }


  /**
   * Track changes to requirements, and reset data.
   */
  private _onRequirementChange(): void {
    this.elementService.element$.pipe(
      skip(1),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this._setData();
    });
  }

  /**
   * Scroll to entity on load.
   *
   * When the url contains a hash (id), scroll to that position
   * on init/load.
   */
  private _scrollToOnInit(): void {
    if (location.hash) {
      this.scrollToService.find(location.hash.substr(1), {
        animate: 'flash',
        offset: -75
      });
    }
  }

  /**
   * Set all data needed for the page.
   */
  private _setData(): void {
    if (this.pagetype === 'area') {
      this.area = this.areaService.getArea(this.params.areaId);
      this.selectedArea = this.areaService.getArea(this.params.areaId);
      this.basicArea = this.basicAreaService.getBasicArea(this.area.basicAreaId);
      this.requirements = this.areaService.getRequirements(this.area._id);
      this.clusters = this.clusterService.get();
    } else {
      this.basicArea = this.basicAreaService.getBasicArea(this.params.basicAreaId);
      this.requirements = this.basicAreaService.getRequirements(this.basicArea._id);
    }
  }

  /**
   * Check whether the template should be build for Area of BasicArea.
   */
  private _setPageType(): void {
    this.pagetype = this.routerService.isActive('area')
      ? 'area'
      : 'basicArea';
  }
}
